import React, { useState, useCallback } from 'react';

import './removePolyfill.ts';

import cx from 'classnames';
import { Button, TextButton } from '@xingternal/button';
import { Headline, BodyCopy } from '@xingternal/typography';
import { FormattedMessage } from 'react-intl';
import type { ShowErrorProps } from '@xingternal/pages-page-error';

import RangeSlider from '@xingternal/malt-range-slider';
import Cropper from 'react-easy-crop';

import getCroppedImg, { CropType } from './utils';

import styles from './styles.css';

type CropImageProps = {
  descriptionCopyKey?: string;
  onCropReady?: () => void;
  noHeadline?: boolean;
  image: string;
  onDragCrop?: () => void;
  onChange: (params: any) => void;
  onDiscard: () => void;
  setCropState?: React.Dispatch<React.SetStateAction<boolean>>;
  cropState?: boolean;
  isUploading?: boolean;
  aspectRatio?: number;
  children?: React.ReactNode;
  leftButton?: React.ReactNode;
  discardCopy?: string;
  publishCopy?: string;
  showError: ShowErrorProps;
};

const CropImage = ({
  noHeadline,
  descriptionCopyKey,
  isUploading,
  image,
  onCropReady,
  onChange,
  onDiscard,
  aspectRatio,
  discardCopy = 'EP_DISCARD_CTA',
  publishCopy = 'EP_PUBLISH_CTA',
  children,
  leftButton = null,
}: CropImageProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropType | null>(
    null
  );
  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const cropImage = useCallback(async () => {
    const croppedImage = await getCroppedImg(image, croppedAreaPixels);

    onChange(croppedImage);
  }, [croppedAreaPixels]);

  return (
    <>
      {!noHeadline && (
        <Headline size={'xxlarge'} className={styles.uploadHeadline}>
          <FormattedMessage
            id="EP_CHOOSE_SECTION"
            defaultMessage="EP_CHOOSE_SECTION"
          />
        </Headline>
      )}
      {descriptionCopyKey && (
        <BodyCopy size={'small'}>
          <span>
            <FormattedMessage
              id={descriptionCopyKey}
              defaultMessage={descriptionCopyKey}
              values={{ b: (t: any) => <b>{t}</b> }}
            />
          </span>
        </BodyCopy>
      )}
      <div>
        <div className={styles.cropContainer} data-cy="CROP_IMAGE_SREEN">
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio || 1 / 1}
            zoomWithScroll={false}
            onCropAreaChange={onCropReady ? () => onCropReady() : undefined}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={false}
          />
        </div>
        <div className={styles.controls}>
          <BodyCopy
            fontWeight={'bold'}
            size={'small'}
            className={styles.sliderText}
          >
            <FormattedMessage id={'EP_MIN'} defaultMessage="EP_MIN" />
          </BodyCopy>
          <RangeSlider
            value={zoom}
            min={1}
            max={2}
            step={0.01}
            showValueLabel={false}
            onChange={(_: any, value: number) => {
              if (value) {
                setZoom(value);
              }
            }}
            direction={'left'}
            type={'continuous'}
            className={styles.slider}
          />
          <BodyCopy
            fontWeight={'bold'}
            size={'small'}
            className={styles.sliderText}
          >
            <FormattedMessage id={'EP_MAX'} defaultMessage="EP_MAX" />
          </BodyCopy>
        </div>
        {children && <div className={styles.childrenContainer}>{children}</div>}
        <div
          className={cx(styles.cropCtaContainer, {
            [styles.cropCtaContainerNoChildren]: !children,
          })}
        >
          {leftButton && <div className={styles.leftButton}>{leftButton}</div>}
          <div className={styles.actionButtons}>
            <TextButton
              size={'medium'}
              className={styles.cancelCta}
              disabled={isUploading}
              onClick={() => onDiscard()}
              data-cy="DISCARD_IMAGE_BUTTON"
            >
              <FormattedMessage
                id={discardCopy}
                defaultMessage="EP_DISCARD_CTA"
              />
            </TextButton>
            <Button
              variant={'primary'}
              size={'medium'}
              loading={isUploading}
              onClick={() => {
                cropImage();
              }}
              data-cy="SAVE_IMAGE_BUTTON"
            >
              <FormattedMessage
                id={publishCopy}
                defaultMessage="EP_PUBLISH_CTA"
              />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CropImage;
